import { useEffect, useMemo } from 'react';
import { useProjectId, useProjectOrganizationId } from 'store/project';
import { userWithPermissionsInDtoToModel } from 'shared/domain/user/mapping/toModel';
import { getRoleFromUserWithPermissionsInDto } from 'shared/domain/user/mapping/userRole';
import { userWithPermissionsInDtoToUserInDto } from 'shared/domain/user/mapping/toOutDto';
import {
  LabelledUser,
  userWithPermissionsInDtoToLabelled,
} from 'shared/domain/user/mapping/toView';
import type {
  UserInDto,
  UserWithPermissionsInDto,
} from 'shared/domain/user/types/inDto';
import { UserModel } from 'shared/domain/user/types/model';
import { UserRole } from 'shared/types/userRole';
import { getUserStoreState, useUserStore } from '../store';

// ─── Logged-in user (independent of the selected project) ───

/**
 * Logged-in user with permissions in all organizations and projects.
 * Destructure what you need: `const { _id, email } = useCurrentUser()`.
 */
export function useCurrentUser(): UserWithPermissionsInDto {
  return useUserStore.use.user();
}

/** Whether the user is logged in. */
export function useIsAuthenticated(): boolean {
  return useUserStore.use.isAuthenticated();
}

/** Applies the permissions of the logged-in user each time the service worker downloads them. */
export function useCurrentUserChangesSubscription(): void {
  const { subscribeToCurrentUserChanges } = useUserStore.use.actions();

  useEffect(() => subscribeToCurrentUserChanges(), [subscribeToCurrentUserChanges]);
}

/** Non-reactive `useCurrentUser` for code outside React (services, stores). */
export function getCurrentUser(): UserWithPermissionsInDto {
  return getUserStoreState().user;
}

// ─── Logged-in user in the selected project ───

/**
 * Role in the selected project: organization-level permissions first, then
 * project permissions. Use for permission checks and guards.
 */
// Computed inside the selector (primitive result), so components re-render
// only when the role changes, not on every change of the user object.
export function useCurrentUserRole(): UserRole {
  const { organizationId, projectId } = useProjectScope();

  return useUserStore((state) =>
    getRoleFromUserWithPermissionsInDto(state.user, organizationId, projectId)
  );
}

// Mappers below create a new object on every call; useMemo keeps the reference
// stable, so consumers can safely use the result in effect/callback deps.

/**
 * Flat user with role, sites and processes for the selected project.
 * Use for access checks, e.g. `canEdit(user, ...)`, chart access.
 */
export function useCurrentUserInProject(): UserInDto {
  const user = useCurrentUser();
  const { organizationId, projectId } = useProjectScope();

  return useMemo(
    () => userWithPermissionsInDtoToUserInDto(user, organizationId, projectId),
    [user, organizationId, projectId]
  );
}

/**
 * Domain model of the user in the selected project.
 * Use as submitter / author of created or modified entities.
 */
export function useCurrentUserInProjectModel(): UserModel {
  const user = useCurrentUser();
  const { organizationId, projectId } = useProjectScope();

  return useMemo(
    () => userWithPermissionsInDtoToModel(user, organizationId, projectId),
    [user, organizationId, projectId]
  );
}

/**
 * `{ _id, label, email, role }` in the selected project.
 * Use to display the user or as the default author in forms.
 */
export function useCurrentUserInProjectLabelled(): LabelledUser {
  const user = useCurrentUser();
  const { organizationId, projectId } = useProjectScope();

  return useMemo(
    () => userWithPermissionsInDtoToLabelled(user, organizationId, projectId),
    [user, organizationId, projectId]
  );
}

function useProjectScope(): { organizationId: string; projectId: string } {
  return {
    organizationId: useProjectOrganizationId(),
    projectId: useProjectId(),
  };
}

